import { httpClient2 } from 'src/infrastructure/admi-client/http-client';

import type {
    AssignmentRequest,
    CreateGroupRequest,
    CreateRequirementFieldRequest,
    FieldAssignment,
    ProductRequirementGroup,
    ProductSummary,
    RequirementField,
    UpdateGroupRequest,
    UpdateRequirementFieldRequest,
} from './product-requirements-config.dto';

const GROUPS = '/v1/product-requirement-groups';

export const productRequirementsConfigService = {
    // ── reads ────────────────────────────────────────────────────────────
    /**
     * Whether the caller is on the editor allowlist. UI affordance only — the
     * backend re-checks on every write (403 otherwise).
     */
    async getPermissions(): Promise<{ canEdit: boolean }> {
        const { data } = await httpClient2.get<{ canEdit: boolean }>('/v1/product-requirement-config/permissions');
        return data;
    },

    async getProducts(): Promise<ProductSummary[]> {
        const { data } = await httpClient2.get<ProductSummary[]>('/v1/products');
        return data;
    },

    /**
     * Full catalog (GEN-653): every group with ALL fields, neutral criticality.
     * `includeEmpty=true` — unlike the REA page, the editor must list a freshly
     * created group before its first field exists, or the create looks failed.
     */
    async getGroups(): Promise<ProductRequirementGroup[]> {
        const { data } = await httpClient2.get<ProductRequirementGroup[]>('/v1/requirement-groups', {
            params: { includeEmpty: true },
        });
        return data;
    },

    async getAssignments(): Promise<FieldAssignment[]> {
        const { data } = await httpClient2.get<FieldAssignment[]>('/v1/product-requirement-field-assignments');
        return data;
    },

    // ── groups ─────────────────────────────────────────────────────────────
    async createGroup(request: CreateGroupRequest): Promise<ProductRequirementGroup> {
        const { data } = await httpClient2.post<ProductRequirementGroup>(GROUPS, request);
        return data;
    },

    async updateGroup(groupId: string, request: UpdateGroupRequest): Promise<ProductRequirementGroup> {
        const { data } = await httpClient2.put<ProductRequirementGroup>(`${GROUPS}/${groupId}`, request);
        return data;
    },

    async deleteGroup(groupId: string): Promise<void> {
        await httpClient2.delete(`${GROUPS}/${groupId}`);
    },

    async reorderGroups(orderedGroupIds: string[]): Promise<void> {
        await httpClient2.put(`${GROUPS}/reorder`, { orderedGroupIds });
    },

    // ── fields ───────────────────────────────────────────────────────────
    async createField(groupId: string, request: CreateRequirementFieldRequest): Promise<RequirementField> {
        const { data } = await httpClient2.post<RequirementField>(`${GROUPS}/${groupId}/fields`, request);
        return data;
    },

    async updateField(
        groupId: string,
        fieldId: string,
        request: UpdateRequirementFieldRequest,
    ): Promise<RequirementField> {
        const { data } = await httpClient2.put<RequirementField>(`${GROUPS}/${groupId}/fields/${fieldId}`, request);
        return data;
    },

    async deleteField(groupId: string, fieldId: string): Promise<void> {
        await httpClient2.delete(`${GROUPS}/${groupId}/fields/${fieldId}`);
    },

    /** Reorders one sibling bucket: `parentFieldId = null` → the group's top-level fields. */
    async reorderFields(groupId: string, parentFieldId: string | null, orderedFieldIds: string[]): Promise<void> {
        await httpClient2.put(`${GROUPS}/${groupId}/fields/reorder`, { parentFieldId, orderedFieldIds });
    },

    // ── product assignments ──────────────────────────────────────────────
    /** Assign (or re-assign to update criticality) — the body carries the per-product criticality (GEN-642). */
    async assignField(productId: string, fieldId: string, request: AssignmentRequest): Promise<void> {
        await httpClient2.put(`/v1/products/${productId}/requirement-fields/${fieldId}`, request);
    },

    async unassignField(productId: string, fieldId: string): Promise<void> {
        await httpClient2.delete(`/v1/products/${productId}/requirement-fields/${fieldId}`);
    },
};
